<template>
  <div class="login-history-container">
    <el-dialog
      v-model="visible"
      title="用户登录历史"
      width="980px"
      destroy-on-close
    >
      <div class="dialog-content">
        <div class="filter-row">
          <el-row :gutter="20">
            <el-col :span="10">
              <el-input
                v-model="filters.username"
                placeholder="搜索用户名"
                clearable
                @clear="handleSearch"
                @keyup.enter="handleSearch"
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
            </el-col>
            <el-col :span="7">
              <el-select
                v-model="filters.success"
                placeholder="登录状态"
                clearable
                @change="handleSearch"
              >
                <el-option label="成功登录" :value="true" />
                <el-option label="失败登录" :value="false" />
              </el-select>
            </el-col>
            <el-col :span="7">
              <el-button type="primary" @click="handleSearch">
                <el-icon><Search /></el-icon> 查询
              </el-button>
              <el-button @click="resetFilters">
                <el-icon><Refresh /></el-icon> 重置
              </el-button>
            </el-col>
          </el-row>
        </div>

        <el-table
          v-loading="loading"
          :data="loginHistories"
          border
          style="width: 100%"
          empty-text="暂无登录记录"
        >
          <el-table-column label="用户ID" prop="user_id" width="80" />
          <el-table-column label="用户名" prop="username" width="120" />
          <el-table-column label="登录状态" width="100">
            <template #default="scope">
              <el-tag :type="scope.row.success ? 'success' : 'danger'">
                {{ scope.row.success ? '成功' : '失败' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="IP地址" prop="ip" width="140" />
          <el-table-column label="设备类型" prop="device_type" width="120" />
          <el-table-column label="浏览器" prop="browser" width="120" />
          <el-table-column label="登录时间" width="180">
            <template #default="scope">
              {{ formatDateTime(scope.row.login_time) }}
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { Search, Refresh } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import request from '@/utils/request';  // 替换为request工具

// 组件属性
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  userId: {
    type: [Number, String],
    default: null
  }
});

// 组件事件
const emit = defineEmits(['update:modelValue']);

// 响应式状态
const visible = ref(props.modelValue);
const loading = ref(false);
const loginHistories = ref([]);
const currentPage = ref(1);
const pageSize = ref(20);
const total = ref(0);
const filters = reactive({
  username: '',
  success: null
});

// 监听visible变化，同步到父组件
watch(visible, (newVal) => {
  emit('update:modelValue', newVal);
});

// 监听父组件传入的modelValue变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal;
  if (newVal) {
    // 当对话框显示时，如果有userId则加载特定用户的历史，否则清空数据
    if (props.userId) {
      loadUserLoginHistory(props.userId, true); // 重置页码
    } else {
      // 没有指定用户ID时，清空数据
      loginHistories.value = [];
      total.value = 0;
    }
  }
});

// 监听userId变化，重新加载数据
watch(() => props.userId, (newVal) => {
  if (newVal && visible.value) {
    // 如果指定了userId，则自动设置筛选条件
    loadUserLoginHistory(newVal, true); // 重置页码
  }
});

// 格式化日期时间
const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '-';
  const date = new Date(dateTimeStr);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
};

// 加载登录历史数据
const loadLoginHistory = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      limit: pageSize.value
    };

    // 添加筛选条件（如果有）
    if (filters.username) {
      params.username = filters.username;
    }

    if (filters.success !== null) {
      params.success = filters.success;
    }
    
    // 如果有指定用户ID，确保添加到查询参数中
    if (props.userId) {
      params.user_id = props.userId;
    }

    // 注意: request.js已配置baseURL: '/api'，所以路径不要带/api前缀
    console.log('发送请求参数:', params);
    const response = await request.get('/auth/admin/login-history', { params });
    console.log('接收到响应:', response.data);
    loginHistories.value = response.data.history || [];
    total.value = response.data.pagination?.total || 0;
  } catch (error) {
    console.error('加载登录历史失败:', error);
    ElMessage.error('加载登录历史失败: ' + (error.response?.data?.message || error.message || '未知错误'));
  } finally {
    loading.value = false;
  }
};

// 加载特定用户的登录历史
const loadUserLoginHistory = async (userId, resetPage = false) => {
  // 只有在明确要求重置页码时才重置
  if (resetPage) {
    currentPage.value = 1;
  }
  
  // 直接使用用户ID进行查询
  const params = {
    page: currentPage.value,
    limit: pageSize.value,
    user_id: userId
  };
  
  if (filters.success !== null) {
    params.success = filters.success;
  }
  
  loading.value = true;
  try {
    // 注意: request.js已配置baseURL: '/api'，所以路径不要带/api前缀
    console.log('发送用户登录历史请求参数:', params);
    const response = await request.get('/auth/admin/login-history', { params });
    console.log('接收到用户登录历史响应:', response.data);
    loginHistories.value = response.data.history || [];
    total.value = response.data.pagination?.total || 0;
  } catch (error) {
    console.error('加载用户登录历史失败:', error);
    ElMessage.error('加载用户登录历史失败: ' + (error.response?.data?.message || error.message || '未知错误'));
  } finally {
    loading.value = false;
  }
};

// 处理搜索
const handleSearch = () => {
  if (props.userId) {
    loadUserLoginHistory(props.userId, true); // 搜索时重置页码
  } else {
    currentPage.value = 1; // 重置页码
    loadLoginHistory();
  }
};

// 重置筛选条件
const resetFilters = () => {
  filters.username = '';
  filters.success = null;
  if (props.userId) {
    loadUserLoginHistory(props.userId, true); // 重置筛选时重置页码
  } else {
    currentPage.value = 1; // 重置页码
    loadLoginHistory();
  }
};

// 处理页码变化
const handleCurrentChange = (page) => {
  currentPage.value = page;
  if (props.userId) {
    loadUserLoginHistory(props.userId, false); // 不重置页码
  } else {
    loadLoginHistory();
  }
};

// 处理每页显示数量变化
const handleSizeChange = (size) => {
  pageSize.value = size;
  currentPage.value = 1; // 改变每页数量时重置为第1页
  if (props.userId) {
    loadUserLoginHistory(props.userId, false); // 不重置页码，因为已经手动设置为1
  } else {
    loadLoginHistory();
  }
};

// 组件挂载时如果是显示状态，则加载数据
onMounted(() => {
  if (visible.value) {
    if (props.userId) {
      loadUserLoginHistory(props.userId, true); // 初次加载时重置页码
    } else {
      // 没有指定用户ID时，保持空数据
      loginHistories.value = [];
      total.value = 0;
    }
  }
});
</script>

<style scoped>
.login-history-container {
  width: 100%;
}

.dialog-content {
  min-height: 300px;
}

.filter-row {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.el-table {
  margin: 15px 0;
}
</style> 